import { Box, IconButton, InputBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from 'config/theme';
import CloseCircleFillIcon from 'remixicon-react/CloseCircleFillIcon';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';
import { useState, useRef, useEffect } from 'react';
import useUserData from 'hooks/useUserData';
import useTrackerStore from '@buildwise/libs/trackers/store/useTrackerStore';
import { useTrackerContext } from '@buildwise/libs/trackers/context/TrackerContext';
import { useTrackerTasksQuery } from '@buildwise/libs/trackers';

function Search() {
  const { companyId } = useUserData();
  const { state, dispatch } = useTrackerContext();
  const { trackerId } = state;
  const { trackerState, setTrackerState } = useTrackerStore();
  const { tasks } = useTrackerTasksQuery({
    trackerId,
    page: 0,
    size: 1000,
  });

  // Get saved searchKeyword from store
  const savedKeyword = trackerState?.[companyId]?.[trackerId]?.searchKeyword ?? '';
  const [value, setValue] = useState(savedKeyword);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local value with store on mount (robust for trackerId/companyId changes)
  useEffect(() => {
    setValue(savedKeyword);
  }, [companyId, trackerId, trackerState]);

  // Always filter on the original tasks list
  const filterTasks = (keyword: string) => {
    if (!keyword) return tasks;
    const lower = keyword.toLowerCase();
    return (tasks || []).filter((task) => {
      return (
        task.title?.toLowerCase().includes(lower) ||
        task.description?.toLowerCase().includes(lower) ||
        task.type?.toLowerCase().includes(lower) ||
        task.createdBy?.toLowerCase().includes(lower) ||
        task.impact?.toLowerCase().includes(lower) ||
        task.applicableTo?.toLowerCase().includes(lower) ||
        task.updatedBy?.toLowerCase().includes(lower) ||
        task.status?.toLowerCase().includes(lower)
      );
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value;
    setValue(keyword);
    setTrackerState(companyId, trackerId, { searchKeyword: keyword });
    const filtered = filterTasks(keyword);
    dispatch({
      payload: { filteredTasks: filtered },
    });
  };

  return (
    <Box>
      <SearchInput
        autoFocus
        inputRef={inputRef}
        value={value}
        onChange={handleChange}
        fullWidth
        placeholder="Search..."
        className={value ? 'has-value' : ''}
        startAdornment={
          <SearchLineIcon size={20} color={theme.palette.custom.steel} style={{ marginRight: 8 }} />
        }
        endAdornment={
          value && (
            <IconButton
              size="small"
              className="clear-icon"
              onClick={() => {
                setValue('');
                setTrackerState(companyId, trackerId, { searchKeyword: '' });
                dispatch({
                  payload: { filteredTasks: tasks },
                });
                inputRef.current?.focus();
              }}
            >
              <CloseCircleFillIcon size={18} />
            </IconButton>
          )
        }
      />
    </Box>
  );
}

const SearchInput = styled(InputBase, { label: 'SearchInput' })(({ theme }) => ({
  minWidth: 250,
  maxWidth: 300,
  width: '250px',
  transition:
    'width 0.3s cubic-bezier(0.4,0,0.2,1), min-width 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)',
  borderRadius: 8,
  border: `1px solid transparent`,
  padding: '4px 16px',
  backgroundColor: theme.palette.custom.white,
  borderColor: `${theme.palette.custom.platinum}`,
  '&:focus-within, &.has-value': {
    borderColor: `${theme.palette.custom.mistBlue} !important`,
    backgroundColor: theme.palette.custom.white,
  },
  '& .MuiInputBase-input': {
    transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
    width: '100%',
  },
  '&.has-value': {
    width: '320px',
    minWidth: 300,
    maxWidth: 300,
  },
  '&:not(.has-value)': {
    width: '200px',
    minWidth: 250,
    maxWidth: 300,
  },
}));

export default Search;
